import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Masonry from 'react-masonry-css';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './Gallery.css';

const Gallery = () => {
  const [filter, setFilter] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const categories = ['all', 'portraits', 'commercial', 'nature'];

  const images = [
    // Portraits
    { id: 1, category: 'portraits', title: 'Portrait 1', src: '/images/portraits/IMG_0113.PNG' },
    { id: 2, category: 'portraits', title: 'Portrait 2', src: '/images/portraits/IMG_0115.PNG' },
    { id: 3, category: 'portraits', title: 'Portrait 3', src: '/images/portraits/IMG_0175.PNG' },
    { id: 4, category: 'portraits', title: 'Portrait 4', src: '/images/portraits/IMG_6693.JPG' },
    { id: 5, category: 'portraits', title: 'Portrait 5', src: '/images/portraits/IMG_9374.JPEG' },
    { id: 6, category: 'portraits', title: 'Portrait 6', src: '/images/portraits/IMG_9972.JPG' },
    { id: 7, category: 'portraits', title: 'Portrait 7', src: '/images/portraits/IMG_9981.JPG' },
    { id: 8, category: 'portraits', title: 'Portrait 8', src: '/images/portraits/IMG_9982.JPG' },
    { id: 9, category: 'portraits', title: 'Portrait 9', src: '/images/portraits/IMG_9986.JPG' },
    { id: 10, category: 'portraits', title: 'Portrait 10', src: '/images/portraits/IMG_9987.JPG' },
    { id: 11, category: 'portraits', title: 'Portrait 11', src: '/images/portraits/IMG_9989.JPG' },
    { id: 12, category: 'portraits', title: 'Portrait 12', src: '/images/portraits/IMG_9993.JPG' },
    // Commercial
    { id: 13, category: 'commercial', title: 'Commercial 1', src: '/images/commercial/IMG_0119.PNG' },
    { id: 14, category: 'commercial', title: 'Commercial 2', src: '/images/commercial/IMG_6830.PNG' },
    { id: 15, category: 'commercial', title: 'Commercial 3', src: '/images/commercial/IMG_7203.JPG' },
    { id: 16, category: 'commercial', title: 'Commercial 4', src: '/images/commercial/IMG_7222.JPG' },
    // Nature
    { id: 17, category: 'nature', title: 'Nature 1', src: '/images/nature/IMG_8493.JPG' },
    { id: 18, category: 'nature', title: 'Nature 2', src: '/images/nature/IMG_8502.JPG' },
    { id: 19, category: 'nature', title: 'Nature 3', src: '/images/nature/IMG_8505.JPG' },
    { id: 20, category: 'nature', title: 'Nature 4', src: '/images/nature/IMG_8508.JPG' },
  ];

  const filteredImages = filter === 'all' 
    ? images 
    : images.filter(img => img.category === filter);

  const breakpointColumns = {
    default: 4,
    1100: 3,
    700: 2,
    500: 1
  };

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const nextIndex = (currentIndex + 1) % filteredImages.length;
    setSelectedImage(filteredImages[nextIndex]);
    setCurrentIndex(nextIndex);
  };

  const prevImage = () => {
    const prevIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setSelectedImage(filteredImages[prevIndex]);
    setCurrentIndex(prevIndex);
  };

  return (
    <section id="gallery" className="gallery">
      <div className="gallery-container">
        <motion.div
          className="gallery-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Our Portfolio</h2>
          <div className="title-underline"></div>
          <p className="section-subtitle">
            Explore our collection of stunning photography across various genres
          </p>
        </motion.div>

        <motion.div
          className="filter-buttons"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {categories.map((category) => (
            <button
              key={category}
              className={`filter-btn ${filter === category ? 'active' : ''}`}
              onClick={() => setFilter(category)}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </motion.div>

        <Masonry
          breakpointCols={breakpointColumns}
          className="masonry-grid"
          columnClassName="masonry-grid-column"
        >
          {filteredImages.map((image, index) => (
            <motion.div
              key={image.id}
              className="gallery-item"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              onClick={() => openLightbox(image, index)}
            >
              <div className="gallery-image">
                <img src={image.src} alt={image.title} />
                <div className="image-overlay">
                  <h3 className="image-title">{image.title}</h3>
                  <p className="image-category">{image.category}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </Masonry>
      </div>

      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            <button className="lightbox-close" onClick={closeLightbox}>
              <FaTimes />
            </button>
            <button className="lightbox-prev" onClick={(e) => { e.stopPropagation(); prevImage(); }}>
              <FaChevronLeft />
            </button>
            <button className="lightbox-next" onClick={(e) => { e.stopPropagation(); nextImage(); }}>
              <FaChevronRight />
            </button>
            <motion.div
              className="lightbox-content"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={selectedImage.src} alt={selectedImage.title} className="lightbox-image" />
              <div className="lightbox-info">
                <h3>{selectedImage.title}</h3>
                <p>{selectedImage.category}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
